import React, { Component } from 'react';
import moment from 'moment';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import BookedSpotsManagementModal from './modals/bookedSpotsManagementModal';

const localizer = momentLocalizer(moment);

class BookedSpots extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedDate: (() => {
                const today = moment();
                const calendarStartDate = moment(props.calendar?.startDate);

                // Use calendar start date if it's in the future, otherwise use today
                if (calendarStartDate.isValid() && calendarStartDate.isAfter(today)) {
                    return calendarStartDate.format('dddd, MMMM D, YYYY');
                }
                return today.format('dddd, MMMM D, YYYY');
            })(),
            selectedTimeSlot: null
        };
    }

    handlePreviousDay = () => {
        const { selectedDate } = this.state;
        const { calendar } = this.props;

        let prevDate = moment(selectedDate, 'dddd, MMMM D, YYYY').subtract(1, 'days');
        const startDate = moment(calendar?.startDate);

        // Don't go before the start date of the calendar
        if (prevDate.isBefore(startDate)) {
            return;
        }

        // Check if the previous day is available in the schedule
        let isAvailable = false;
        let attempts = 0;
        const maxAttempts = 30; // Prevent infinite loop

        while (!isAvailable && attempts < maxAttempts && prevDate.isSameOrAfter(startDate)) {
            const dayOfWeek = prevDate.format('dddd').toLowerCase();
            const dayConfig = calendar.schedule?.[dayOfWeek];

            if (dayConfig?.available && dayConfig.availableTimeRanges?.length > 0) {
                isAvailable = true;
            } else {
                prevDate.subtract(1, 'days');
            }
            attempts++;
        }

        this.setState({
            selectedDate: prevDate.format('dddd, MMMM D, YYYY')
        });
    };

    handleNextDay = () => {
        const { selectedDate } = this.state;
        const { calendar } = this.props;

        let nextDate = moment(selectedDate, 'dddd, MMMM D, YYYY').add(1, 'days');
        const endDate = moment(calendar?.endDate);

        // Don't go past the end date of the calendar
        if (nextDate.isAfter(endDate)) {
            return;
        }

        // Check if the next day is available in the schedule
        let isAvailable = false;
        let attempts = 0;
        const maxAttempts = 30; // Prevent infinite loop

        while (!isAvailable && attempts < maxAttempts && nextDate.isSameOrBefore(endDate)) {
            const dayOfWeek = nextDate.format('dddd').toLowerCase();
            const dayConfig = calendar.schedule?.[dayOfWeek];

            if (dayConfig?.available && dayConfig.availableTimeRanges?.length > 0) {
                isAvailable = true;
            } else {
                nextDate.add(1, 'days');
            }
            attempts++;
        }

        this.setState({
            selectedDate: nextDate.format('dddd, MMMM D, YYYY')
        });
    };

    toggleTimeSlot = (slot) => {
        this.setState({ selectedTimeSlot: slot });
    }

    renderTimeSlots = () => {
        const { calendar, bookingPage, durationUnit } = this.props;
        const { selectedDate } = this.state;
        const slots = [];

        const startDate = moment(calendar?.startDate);
        const endDate = moment(calendar?.endDate);
        const currentDate = moment(selectedDate, 'dddd, MMMM D, YYYY');

        // Check if the selected date is past the end date
        if (currentDate.isAfter(endDate)) {
            return <div className="text-center gray">This date is after the calendar's end date.</div>;
        }

        const dayOfWeek = currentDate.format('dddd').toLowerCase();
        const dayConfig = calendar.schedule?.[dayOfWeek];

        if (dayConfig?.available && dayConfig.availableTimeRanges?.length > 0) {
            const formattedDate = currentDate.format('MMMM Do, YYYY');

            dayConfig.availableTimeRanges.forEach((range, idx) => {
                const startTime = moment(`${currentDate.format('YYYY-MM-DD')} ${range.startTime}`, 'YYYY-MM-DD h:mm A');
                const endTime = moment(`${currentDate.format('YYYY-MM-DD')} ${range.endTime}`, 'YYYY-MM-DD h:mm A');

                let slotTime = moment(startTime);

                while (slotTime.isBefore(endTime)) {
                    const slotStr = slotTime.format('h:mm A');
                    // Check if this slot is fully booked
                    const slotDate = currentDate.format('YYYY-MM-DD');
                    const slotTimeStr = slotTime.format('h:mm A');
                    const endTimeStr = slotTime.clone().add(bookingPage.meetingDuration, bookingPage.durationUnit).format('h:mm A');
                    const durationText = `${bookingPage.meetingDuration} ${bookingPage.durationUnit}`;

                    // Find booking info for this slot if it exists
                    const booking = calendar.bookedSpots?.find(
                        spot => spot.date === slotDate && spot.time === slotTimeStr
                    );

                    // Count how many bookings already exist for this time slot
                    const bookingsCount = calendar.bookedSpots?.filter(
                        spot => {
                            let bookedSpotTime = spot.time;
                            let bookedSpotDate = spot.date;
                            return bookedSpotDate === slotDate &&
                                bookedSpotTime === slotTimeStr &&
                                spot.status !== 'canceled';
                        }
                    )?.length || 0;

                    const maxSpots = calendar.maxSpots || 1;

                    let isAvailable = calendar.limitSpots ? bookingsCount < maxSpots : true;

                    // Calculate if slot is overbooked
                    const isOverbooked = bookingsCount > maxSpots;

                    slots.push(
                        <div
                            className="neuCard p-15"
                            key={`${slotDate}-${slotTimeStr}`}
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                            onClick={() => {
                                this.toggleTimeSlot({
                                    date: slotDate,
                                    startTime: slotTimeStr,
                                    endTime: endTimeStr,
                                    duration: durationText,
                                    bookingsCount,
                                    maxSpots,
                                    isAvailable,
                                    isOverbooked
                                });
                            }}
                        >
                            <div className="flex jcsb aic">
                                <div className="flex aic gap15">
                                    <div className="timeSlotInfo">
                                        <div className="timeSlot">
                                            <strong>{slotTimeStr} - {endTimeStr}</strong>
                                            {isOverbooked &&
                                                <span
                                                    className="overbooked-bubble"
                                                    style={{
                                                        display: 'inline-block',
                                                        marginLeft: '8px',
                                                        width: '12px',
                                                        height: '12px',
                                                        backgroundColor: '#ff4c4c',
                                                        borderRadius: '50%',
                                                        animation: 'pulse 2s infinite'
                                                    }}
                                                    title="This slot is overbooked"
                                                />
                                            }
                                        </div>
                                        <div className="duration text-sm gray">{durationText}</div>
                                        <div className="booking-count text-sm">
                                            <span className={`status-badge ${isOverbooked ? 'overbooked' : bookingsCount >= maxSpots ? 'filled' : 'available'}`}>
                                                {bookingsCount}/{maxSpots} {isOverbooked ? 'OVERBOOKED' : bookingsCount >= maxSpots ? 'FULL' : 'spots filled'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );

                    slotTime.add(bookingPage.meetingDuration, bookingPage.durationUnit);
                }
            });
        }

        return slots.length > 0 ? slots : <div className="text-center gray">No available time slots for this date.</div>;
    }


    render() {
        const { calendar } = this.props;
        const { selectedDate, selectedTimeSlot } = this.state;


        return (

            <div className="sectionBody pt-0">
                <div className="sectionBlock">
                    <h2 className="blockHeader">Booked Spots</h2>
                    <p className="small mb-20">View all confirmed bookings for this calendar with registrant details.</p>
                    <div className="mb-20">
                        <div className="d-flex align-items-center mb-15">
                            <button onClick={this.handlePreviousDay} className="btn p-0 ml-a">
                                <i className="fa fa-chevron-left"></i>
                            </button>
                            <h3 className="m-0 text-c w-100">{selectedDate}</h3>
                            <button onClick={this.handleNextDay} className="btn  p-0 mr-a">
                                <i className="fa fa-chevron-right"></i>
                            </button>
                        </div>
                    </div>
                    <div className="flex column gap10">
                        {this.renderTimeSlots()}
                    </div>
                </div>


                {selectedTimeSlot !== null &&

                    <BookedSpotsManagementModal
                        isOpen={selectedTimeSlot !== null}
                        toggle={() => this.toggleTimeSlot(null)}
                        selectedTimeSlot={selectedTimeSlot}
                        updateBookedTimeslot={this.props.updateBookedTimeslot}
                        {...this.props}
                    />
                }
            </div>

        );
    }
}

export default BookedSpots;
